<script>
import { mapState, mapActions } from 'vuex'

export default {
   data() {
      return {
         fields: [
            { key: 'rank', label: '#', sortable: false },
            { key: 'nama', label: 'Sektor', sortable: true },
            { key: 'total', label: 'Total', sortable: true },
         ],
         loading: false,
      }
   },
   computed: {
      ...mapState({
         sektorData: state => state.DashboardLkpmNonUmk.nonUmkSektor,
         tahun: state => state.DashboardLkpmNonUmk.tahun,
         refreshTimestamp: state => state.DashboardLkpmNonUmk.refreshTimestamp,
         skin: state => state.appConfig.layout.skin,
      }),
      isDark() {
         return this.skin === 'dark'
      },
      rankedData() {
         return (this.sektorData.data || [])
            .slice()
            .sort((a, b) => b.total - a.total)
            .slice(0, 10)
            .map((item, index) => ({
               ...item,
               rank: index + 1,
               color: this.sektorColors[index % this.sektorColors.length],
            }))
      },
      sektorColors() {
         return [
            '#9f1e15', '#28c76f', '#7367f0', '#ff9f43', '#00cfe8',
            '#ea5455', '#1d4ed8', '#8b5cf6', '#f59e0b', '#10b981',
         ]
      },
      maxTotal() {
         if (!this.rankedData.length) return 100
         return Math.max(...this.rankedData.map(item => item.total))
      },
   },
   watch: {
      tahun(newYear) {
         this.loadData(newYear)
      },
      refreshTimestamp() {
         this.loadData(this.tahun)
      },
   },
   mounted() {
      this.loadData(this.tahun)
   },
   methods: {
      ...mapActions({
         getNonUmkSektor: 'DashboardLkpmNonUmk/getNonUmkSektor',
      }),
      async loadData(year) {
         this.loading = true
         try {
            await this.getNonUmkSektor(year)
         } catch (error) {
            // Tabel tetap menampilkan data kosong saat request gagal.
         } finally {
            this.loading = false
         }
      },
      getProgressWidth(total) {
         if (this.maxTotal === 0) return '0%'
         return `${(total / this.maxTotal) * 100}%`
      },
   },
}
</script>

<template>
   <b-overlay
      :show="loading"
      :variant="isDark ? 'dark' : 'white'"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
   >
      <b-row>
         <b-col cols="12">
            <b-card
               class="card-wrapper"
               :class="{ 'card-wrapper-dark': isDark }"
            >
               <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-1">
                  <div class="mb-1 mb-md-0">
                     <h4 class="mb-25">
                        Top 10 Sektor Non UMK Tahun {{ tahun }}
                     </h4>
                     <small class="text-muted">Total Realisasi Non UMK tertinggi berdasarkan sektor</small>
                  </div>
               </div>

               <!-- Desktop View -->
               <div class="d-none d-md-block">
                  <b-table
                     :items="rankedData"
                     :fields="fields"
                     responsive
                     class="sektor-table"
                  >
                     <template #cell(rank)="data">
                        <div
                           class="rank-badge d-flex align-items-center justify-content-center font-weight-bolder"
                           :style="{
                              background: data.item.color + '20',
                              color: data.item.color,
                           }"
                        >
                           {{ data.item.rank }}
                        </div>
                     </template>

                     <template #cell(nama)="data">
                        <div class="d-flex align-items-center">
                           <span
                              class="sektor-dot mr-1"
                              :style="{ background: data.item.color }"
                           />
                           <span class="text-wrap font-weight-bolder">{{ data.item.nama }}</span>
                        </div>
                     </template>

                     <template #cell(total)="data">
                        <div class="d-flex align-items-center">
                           <div class="progress-wrapper mr-1">
                              <div
                                 class="progress-bar-custom"
                                 :style="{
                                    width: getProgressWidth(data.item.total),
                                    background: data.item.color,
                                 }"
                              />
                           </div>
                           <span class="font-weight-bolder">
                              {{ data.item.total.toLocaleString('id-ID') }}
                           </span>
                        </div>
                     </template>
                  </b-table>
               </div>

               <!-- Mobile View -->
               <div class="d-md-none">
                  <b-list-group>
                     <b-list-group-item
                        v-for="item in rankedData"
                        :key="item.nama"
                        class="sektor-card-item"
                     >
                        <div class="d-flex justify-content-between align-items-start mb-1">
                           <div class="d-flex align-items-center">
                              <div
                                 class="rank-badge-sm mr-2 font-weight-bolder"
                                 :style="{
                                    background: item.color + '20',
                                    color: item.color,
                                 }"
                              >
                                 {{ item.rank }}
                              </div>
                              <div class="d-flex align-items-center">
                                 <span class="sektor-dot mr-1" :style="{ background: item.color }" />
                                 <span class="font-weight-bolder">{{ item.nama }}</span>
                              </div>
                           </div>
                           <div
                              class="font-weight-bolder ml-2"
                              :style="{ color: item.color }"
                           >
                              {{ item.total.toLocaleString('id-ID') }}
                           </div>
                        </div>
                        <div class="progress-bar-custom" :style="{ width: getProgressWidth(item.total), background: item.color }" />
                     </b-list-group-item>
                  </b-list-group>
               </div>
            </b-card>
         </b-col>
      </b-row>
   </b-overlay>
</template>

<style scoped>
.card-wrapper {
   border: 0;
   border-radius: 12px;
   box-shadow: 0 4px 24px rgba(34, 41, 47, 0.08);
   background-color: #ffffff;
}

.card-wrapper-dark {
   background-color: #283046;
   box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
}

.card-wrapper-dark .sektor-card-item {
   border-bottom-color: #3b4253;
   background: transparent;
}

.rank-badge {
   width: 32px;
   height: 32px;
   border-radius: 8px;
   font-size: 0.9rem;
}

.rank-badge-sm {
   width: 28px;
   height: 28px;
   border-radius: 6px;
   font-size: 0.85rem;
   display: flex;
   align-items: center;
   justify-content: center;
}

.sektor-dot {
   width: 10px;
   height: 10px;
   border-radius: 50%;
   flex-shrink: 0;
}

.progress-wrapper {
   width: 100px;
   height: 8px;
   background: #e9ecef;
   border-radius: 4px;
   overflow: hidden;
   flex-shrink: 0;
}

.progress-bar-custom {
   height: 100%;
   border-radius: 4px;
   transition: width 0.3s ease;
}

.sektor-card-item {
   border: 0;
   border-bottom: 1px solid #f1f1f2;
   padding: 0.75rem 0;
   background: transparent;
}

.sektor-card-item:last-child {
   border-bottom: 0;
}

.sektor-table :deep(.table) {
   margin-bottom: 0;
}

.sektor-table :deep(th) {
   border-top: none;
   font-size: 0.85rem;
   color: #6e6b7b;
}

.sektor-table :deep(td) {
   vertical-align: middle;
}
</style>
